import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useFetch } from "../lib/api";
import { Empty } from "./shared";
import { SitePanel } from "./SitePanel";

const GRADE = { A: "#00E599", B: "#34D399", C: "#FBBF24", D: "#F97316", F: "#F43F5E" };
const PALETTE = ["#00E599", "#38BDF8", "#FBBF24", "#F472B6", "#A78BFA", "#F97316", "#2DD4BF", "#F43F5E", "#84CC16", "#E879F9"];
const scoreColor = (s) => (s.status === "PENDING" ? "#94A3B8" : GRADE[s.grade] || "#64748B");

const Fit = ({ pts }) => {
  const map = useMap();
  const key = pts.map((p) => p.join(",")).join("|");
  useEffect(() => {
    if (pts.length === 1) map.setView(pts[0], 11);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [40, 40], maxZoom: 11 });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const Pins = ({ placed, sel, setSel, color, month }) => {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useEffect(() => { const f = () => setZoom(map.getZoom()); map.on("zoomend", f); return () => map.off("zoomend", f); }, [map]);
  const pos = useMemo(() => {
    const pts = placed.map((s) => map.project([s.lat, s.lng], zoom)), out = [];
    pts.forEach((p, i) => {
      const near = out.filter((q) => q.base.distanceTo(p) < 24).length;
      const a = near * 2.4, d = near ? 22 + near * 3 : 0;
      out.push({ base: p, at: map.unproject(p.add([Math.cos(a) * d, Math.sin(a) * d]), zoom), s: placed[i] });
    });
    return out;
  }, [placed, zoom, map]);
  return pos.map(({ at, s }) => (
    <CircleMarker key={s.id} center={at} radius={9 + Math.min(10, s.workers / 5)} eventHandlers={{ click: () => setSel(s.id) }}
      pathOptions={{ color: sel === s.id ? "#FFFFFF" : "#0B1017", weight: sel === s.id ? 3 : 2, fillColor: color(s), fillOpacity: 0.9 }}>
      <Popup><div className="text-sm" data-testid={`map-popup-${s.id}`}><b>{s.name}</b><br />{[s.city, s.state].filter(Boolean).join(", ")}<br />{s.vendor || "No contractor"} · {s.workers} worker(s)<br />
        {s.status === "PENDING" ? "Waiting approval" : s.score != null ? `Score ${s.score} (grade ${s.grade}) · ${month}` : `No score yet for ${month}`}</div></Popup>
    </CircleMarker>));
};

export const SiteMap = ({ status }) => {
  const { data } = useFetch("/sites/map");
  const [by, setBy] = useState("score");
  const [sel, setSel] = useState(null);
  const sites = (data?.sites || []).filter((s) => !status || s.status === status);
  const placed = sites.filter((s) => s.lat != null);
  const states = useMemo(() => [...new Set(placed.map((s) => s.state || "No state"))].sort(), [placed]);
  const color = (s) => (by === "score" ? scoreColor(s) : PALETTE[states.indexOf(s.state || "No state") % PALETTE.length]);
  if (!data) return <Empty text="Loading map…" />;
  const legend = by === "score"
    ? [["A", GRADE.A], ["B", GRADE.B], ["C", GRADE.C], ["D", GRADE.D], ["F", GRADE.F], ["No score yet", "#64748B"], ["Waiting approval", "#94A3B8"]]
    : states.map((s, i) => [s, PALETTE[i % PALETTE.length]]);
  return (
    <div className="c-surface c-rise p-4" data-testid="site-map">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="text-xs text-slate-400">Colour by</span>
        {[["score", "Contractor filing score"], ["state", "State"]].map(([k, l]) => (
          <button key={k} onClick={() => setBy(k)} data-testid={`map-colour-${k}`} className={`rounded-full border px-3 py-1 text-xs ${by === k ? "border-[#00E599]/40 bg-[#00E599]/10 text-[#00E599]" : "border-[#1F2A3E] text-slate-400"}`}>{l}</button>))}
        <div className="flex flex-wrap gap-3 ml-auto" data-testid="map-legend">{legend.map(([l, c]) => <span key={l} className="flex items-center gap-1.5 text-[11px] text-slate-300"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />{l}</span>)}</div>
      </div>
      <div className={`grid gap-4 ${sel ? "lg:grid-cols-[1fr_360px]" : ""}`}>
      <div className="h-[520px] rounded-lg overflow-hidden border border-[#1F2A3E] isolate">
        <MapContainer center={[22.5, 79]} zoom={5} scrollWheelZoom className="h-full w-full" style={{ background: "#0B1017" }}>
          <TileLayer className="c-dark-tiles" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Fit pts={placed.map((s) => [s.lat, s.lng])} />
          <Pins placed={placed} sel={sel} setSel={setSel} color={color} month={data.month} />
        </MapContainer>
      </div>
      {sel && <SitePanel key={sel} id={sel} onClose={() => setSel(null)} />}
      </div>
      {!sel && placed.length > 0 && <p className="text-[11px] text-slate-500 mt-2">Tap a pin to see that site's workers, open issues and payment holds.</p>}
      {sites.length > placed.length && <p className="text-xs text-amber-300 mt-2" data-testid="map-unplaced">{sites.length - placed.length} site(s) not on the map. Add a PIN code or use "Use my location" on the site.</p>}
    </div>);
};
